import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import {
  Box, Checkbox, Paper, Tooltip, Typography,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import HoldingModal from 'Components/Modals/HoldingModal';
import { useHistory } from 'react-router';
import HoldingsAccordion from 'Components/UI/Snapshot/Holdings/HoldingsAccordion';
import { InfoOutlined as InfoOutlinedIcon } from '@material-ui/icons';
import {
  usePortfolioStateContext,
  usePortfolioDispatchContext,
} from 'Components/UI/Portfolio/PortfolioContext';
import { refreshPortfolioCollection } from 'Components/UI/Portfolio/PortfolioActions';
import useSortedAndFilteredAccounts from './useSortedAndFilteredAccounts';

const accountModalIdQueryParam = 'accoutModalId';
const holdingModalIdQueryParam = 'holdingModalId';

const styles = theme => ({
  noAccountsText: {
    width: '100%',
  },
  header: {
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'space-between',
  },
  tableContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  accountSection: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: '1px solid',
    borderColor: theme.palette.facet.lightGray,
    margin: theme.spacing(1),
  },
  hideAccounts: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  expenses: {
    display: 'flex',
    flexFlow: 'row',
    paddingTop: theme.spacing(2),
  },
  expensesPaid: {
    marginLeft: theme.spacing(2),
  },
  expensesAmount: {
    marginLeft: theme.spacing(1),
    fontWeight: theme.typography.fontWeightBold,
  },
  expensesInfo: {
    marginLeft: theme.spacing(1),
    cursor: 'pointer',
  },
});

function HoldingsAccountList({
  title,
  classes,
  className,
  excludedColumns,
  hideExpensesPaidTooltip,
  portfolio,
  ...rest
}) {
  const state = usePortfolioStateContext();
  const { loading } = state;
  const dispatch = usePortfolioDispatchContext();

  const history = useHistory();
  const [modalOpen, setModalOpen] = useState(false);
  const [currentHolding, setCurrentHolding] = useState(null);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [
    hideAccountsWithoutHoldings,
    setHideAccountsWithoutHoldings,
  ] = useState(true);
  const [disableTable, setDisableTable] = useState(false);
  const sortedAccounts = useSortedAndFilteredAccounts(
    portfolio?.attr('accounts'),
    hideAccountsWithoutHoldings,
  );
  const refreshPortfolio = () => refreshPortfolioCollection(dispatch, state);

  const { location } = history;

  const handleOpenModal = (account, holding) => () => {
    setCurrentAccount(account);
    setCurrentHolding(holding);
    const query = new URLSearchParams(location.search);
    if (account) {
      query.set(accountModalIdQueryParam, account);
    } else {
      query.delete(accountModalIdQueryParam);
    }
    if (holding) {
      query.set(holdingModalIdQueryParam, holding);
    } else {
      query.delete(holdingModalIdQueryParam);
    }

    history.push({
      search: query.toString(),
    });
    setModalOpen(true);
  };

  // Open modal from URL query params
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const queryAccountId = query.get(accountModalIdQueryParam);
    const queryHoldingId = query.get(holdingModalIdQueryParam);
    if (queryAccountId) {
      handleOpenModal(queryAccountId, queryHoldingId)();
    }
  }, [location.search]);

  if (!sortedAccounts) {
    return (
      <Paper className={classNames(classes.tableContainer, className)}>
        <div className={classes.header}>
          <Typography variant="h3" color="primary">
            Account Holdings
          </Typography>
        </div>
        {Array(5).fill(<Skeleton animation="wave" height={64} />)}
      </Paper>
    );
  }

  const handleCloseModal = () => {
    setModalOpen(false);
    const query = new URLSearchParams(location.search);
    query.delete(accountModalIdQueryParam);
    query.delete(holdingModalIdQueryParam);
    history.push({
      pathname: history.location.pathname,
      search: query.toString(),
    });
    refreshPortfolio();
  };

  const AccountsComponent = sortedAccounts.length ? (
    sortedAccounts.map(({ id, accounts: accs }) => {
      return (
        <div key={id}>
          {accs.map((account) => {
            return (
              <HoldingsAccordion
                key={`${account.resourceId}`}
                accountResource={account}
                onOpenModal={handleOpenModal}
                excludedColumns={excludedColumns}
                refresh={refreshPortfolio}
                setDisableTable={setDisableTable}
                disableTable={disableTable}
                {...rest}
              />
            );
          })}
        </div>
      );
    })
  ) : (
    <Typography
      className={classes.noAccountsText}
      variant="h4"
      component="p"
      color="primary"
    >
      No Accounts to display
    </Typography>
  );

  return (
    <Paper className={classNames(classes.tableContainer, className)}>
      {modalOpen && (
        <HoldingModal
          accountId={currentAccount}
          holdingId={currentHolding}
          handleClose={handleCloseModal}
        />
      )}
      <div className={classes.header}>
        <Typography variant="h3" color="primary">
          {title}
        </Typography>
        <div className={classes.expenses}>
          <Typography variant="h5" color="primary" component="p">
            Expense Ratio:
          </Typography>
          <Typography
            variant="h5"
            color="primary"
            component="p"
            className={classes.expensesAmount}
          >
            {`${portfolio.attr('expenseRatio')}`}
          </Typography>
          <Box display="flex">
            <Typography
              variant="h5"
              color="primary"
              component="p"
              className={classes.expensesPaid}
            >
              Expenses Paid:
            </Typography>
            <Typography
              variant="h5"
              color="primary"
              component="p"
              className={classes.expensesAmount}
            >
              {`${portfolio.attr('expensesPaid')}`}
            </Typography>
            {!hideExpensesPaidTooltip && (
              <Tooltip
                title="The 'Expenses Paid' amount is calculated using the total of account balances in the current filter set. This the same amount as  the value of the recommended portfolio.  This is done so expenses paid can be directly compared to the Facet Recommended portfolio."
                placement="top"
                arrow
              >
                <InfoOutlinedIcon
                  fontSize="small"
                  color="primary"
                  className={classes.expensesInfo}
                />
              </Tooltip>
            )}
          </Box>
        </div>
      </div>
      <div className={classes.hideAccounts}>
        <Checkbox
          size="small"
          checked={hideAccountsWithoutHoldings}
          onClick={() => setHideAccountsWithoutHoldings(!hideAccountsWithoutHoldings)
          }
        />
        <Typography variant="h6" component="p" color="primary">
          Hide Accounts without Holdings?
        </Typography>
      </div>
      {loading ? (
        <>{Array(10).fill(<Skeleton animation="wave" height={64} />)}</>
      ) : (
        AccountsComponent
      )}
    </Paper>
  );
}

HoldingsAccountList.defaultProps = {
  className: '',
  excludedColumns: [],
  title: 'Account Holdings',
  hideExpensesPaidTooltip: false,
};

HoldingsAccountList.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  portfolio: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  title: PropTypes.string,
  excludedColumns: PropTypes.array,
  hideExpensesPaidTooltip: PropTypes.bool,
};

export default withStyles(styles)(HoldingsAccountList);
